import React from 'react';
import { Popover } from '@headlessui/react';
import ChevronUp from 'svg/ChevronUp';
import classNames from 'styles/utils/classNames';

export interface FilterOption {
  value: string;
  label: string;
}

interface Props {
  /** Display label shown on the trigger button */
  label: string;
  /** List of selectable options */
  options: FilterOption[];
  /** Currently selected value */
  selectedValue: string;
  /** Callback when a new option is selected */
  onValueChange: (value: string) => void;
  /** Horizontal alignment of the dropdown panel relative to the trigger */
  openPosition?: 'left' | 'right' | 'center';
  /** Use inverted (light) text colors for use on dark backgrounds */
  inverted?: boolean;
}

const POSITION_CLASS: Record<NonNullable<Props['openPosition']>, string> = {
  left: 'left-0',
  right: 'right-0',
  center: 'left-1/2 -translate-x-1/2',
};

/**
 * Reusable popover-based filter dropdown.
 * Renders a trigger button with the selected label + chevron,
 * and a popover panel with selectable options.
 */
export default function FilterDropdown({
  label,
  options,
  selectedValue,
  onValueChange,
  openPosition = 'right',
  inverted = false,
}: Props) {
  const selectedLabel = options.find((o) => o.value === selectedValue)?.label || label;

  return (
    <Popover className="relative">
      {({ open, close }) => (
        <>
          <Popover.Button className="typography-product-button-label-medium flex items-center gap-2 whitespace-nowrap outline-none">
            <span
              className={
                inverted
                  ? 'text-color-text-lightmode-invert dark:text-color-text-darkmode-invert'
                  : 'text-color-text-lightmode-primary dark:text-color-text-darkmode-primary'
              }
            >
              {selectedLabel}
            </span>
            <ChevronUp
              className={classNames(
                'h-4 w-4 shrink-0 rotate-180 transition-transform',
                open && '!rotate-0',
                inverted
                  ? 'text-color-text-lightmode-invert dark:text-color-text-darkmode-invert'
                  : 'text-color-text-lightmode-primary dark:text-color-text-darkmode-primary',
              )}
            />
          </Popover.Button>
          <Popover.Panel className={classNames('absolute z-10 mt-2 w-[15rem] rounded-3xl border border-color-border-input-lightmode bg-color-bg-lightmode-primary p-ds-lg shadow-[0px_4px_20px_0px_rgba(0,0,0,0.16)] dark:border-color-border-input-darkmode dark:bg-color-bg-darkmode-primary', POSITION_CLASS[openPosition])}>
            <div className="flex flex-col gap-1">
              {options.map((option) => {
                const isSelected = selectedValue === option.value;
                return (
                  <button
                    key={option.value}
                    onClick={() => {
                      onValueChange(option.value);
                      close();
                    }}
                    className={classNames(
                      'typography-product-button-label-medium flex h-[2.625rem] items-center rounded-xl border px-4 py-2.5 text-left transition-all',
                      isSelected
                        ? 'border-transparent bg-color-bg-lightmode-invert text-color-text-lightmode-invert dark:bg-color-bg-darkmode-invert dark:text-color-text-darkmode-invert'
                        : 'border-transparent text-color-text-lightmode-secondary hover:border-color-bg-lightmode-icon dark:text-color-text-darkmode-secondary hover:dark:border-color-bg-darkmode-icon',
                    )}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </Popover.Panel>
        </>
      )}
    </Popover>
  );
}
